import React from "react";
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import type { SortOrder } from "./utils";
import { formatHours } from "./utils";
import { PAGE_SIZE } from "./utils";

export interface ColumnConfig {
  label: string;
  key: string;
  format?: "hours" | "text";
}

export interface ScheduledReportsInsightsTableProps {
  title: string;
  count: number;
  columns: ColumnConfig[];
  /** Rows as objects (DTOs or Record); accessed by column key. */
  rows: object[];
  totalCount: number;
  sort: { column: string | null; order: SortOrder };
  onSort: (column: string) => void;
  page: number;
  onPageChange: (page: number) => void;
}

export const ScheduledReportsInsightsTable: React.FC<ScheduledReportsInsightsTableProps> = ({
  title,
  count,
  columns,
  rows,
  totalCount,
  sort,
  onSort,
  page,
  onPageChange,
}) => (
  <Stack spacing={2} sx={{ width: "100%" }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "rgba(255,255,255,0.7)" }}>
      {title} ({count})
    </Typography>
    <TableContainer component={Paper}>
      <Table
        size="small"
        sx={{
          "& .MuiTableCell-root": {
            borderColor: "rgba(255,255,255,0.1)",
            py: 2,
          },
        }}
      >
        <TableHead>
          <TableRow>
            {columns.map(({ label, key }) => (
              <TableCell key={key}>
                <TableSortLabel
                  active={sort.column === key}
                  direction={sort.column === key ? sort.order : "asc"}
                  onClick={() => onSort(key)}
                >
                  <strong>{label}</strong>
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow key={idx}>
              {columns.map(({ key, format }) => {
                const value = (row as Record<string, unknown>)[key];
                return (
                  <TableCell key={key}>
                    {format === "hours"
                      ? formatHours(Number(value) || 0)
                      : String(value ?? "")}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={totalCount}
        page={page - 1}
        onPageChange={(_, p) => onPageChange(p + 1)}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[]}
      />
    </TableContainer>
  </Stack>
);
